import type { StepState } from '@fluxora/ui';

/**
 * Funnel progress derivation (Phase 1 storefront UI).
 *
 * The storefront funnel runs in a fixed, ROUTE-DERIVED order — note that in our
 * flow the credit/approval step comes BEFORE the personalised estimate, which is
 * the opposite of a typical "shop → quote → sign" order:
 *
 *   Plan (`/plan/:slug`)
 *     → Approval / credit (`/plan/:slug/credit`)
 *     → Estimate          (`/plan/:slug/estimate`)
 *     → Sign              (`/plan/:slug/sign`)
 *
 * ── NO "SERVICE ADDRESS" STEP (SHOP-6, partial) ────────────────────────────
 * The meter/address is resolved UP FRONT at the meter step (landing → ESI/ZIP)
 * and carried in `FunnelProvider` (the opaque `candidateId` + display meter). The
 * old mid-funnel `/plan/:slug/address` step is removed from this order; the funnel
 * steps read the resolved candidate from `FunnelProvider` (URL `?candidate=` is a
 * back-compat fallback).
 *
 * This module is a PURE mapping from the current pathname to per-step display
 * state. It holds no i18n (labels are supplied by the caller) and no React — the
 * `@fluxora/ui` Stepper stays domain-blind, and the caller (FunnelStepper) owns
 * the translation.
 */

export type FunnelStepId = 'plan' | 'credit' | 'estimate' | 'sign';

/** The canonical, on-screen left-to-right order of the funnel. */
export const FUNNEL_STEP_ORDER: readonly FunnelStepId[] = [
  'plan',
  'credit',
  'estimate',
  'sign',
] as const;

export interface FunnelStepView {
  readonly id: FunnelStepId;
  readonly state: StepState;
}

/**
 * Which funnel step the given pathname represents, or `undefined` when the path
 * is not a funnel route (e.g. the shelf `/`, or the status page). Matching is on
 * the trailing segment so query strings and a trailing slash are tolerated.
 */
export function currentFunnelStep(pathname: string): FunnelStepId | undefined {
  if (/\/credit\/?$/.test(pathname)) return 'credit';
  if (/\/estimate\/?$/.test(pathname)) return 'estimate';
  if (/\/sign\/?$/.test(pathname)) return 'sign';
  if (/^\/plan\/[^/]+\/?$/.test(pathname)) return 'plan';
  return undefined;
}

/**
 * Maps the current pathname to the full ordered list of step views. Steps before
 * the active one are `done`, the current one is `active`, and later ones are
 * `pending`. Off-funnel paths yield an all-`pending` list (the caller decides not
 * to render in that case).
 */
export function deriveFunnelSteps(pathname: string): readonly FunnelStepView[] {
  const current = currentFunnelStep(pathname);
  const activeIdx = current === undefined ? -1 : FUNNEL_STEP_ORDER.indexOf(current);

  return FUNNEL_STEP_ORDER.map((id, idx): FunnelStepView => {
    if (activeIdx === -1) return { id, state: 'pending' };
    if (idx < activeIdx) return { id, state: 'done' };
    if (idx === activeIdx) return { id, state: 'active' };
    return { id, state: 'pending' };
  });
}
